"use client";

import { BlocksLifecycleTeaser } from "../../../../__generated__/contentful-types";
import { useImageLoading } from "../../../../helpers/ImageLoadingContext";
import { useKickerContext } from "../../../../helpers/contentful/KickerContext";
import { getContentfulVideoUrl } from "../../../../helpers/contentful/getContentfulVideoUrl";
import { getContentfulImageUrl } from "../../../../helpers/contentful/images/getContentfulImageUrl";
import { notNullish } from "../../../../helpers/notNullish";
import { Kicker } from "../../../kicker/kicker";
import {
  ExplodedWordsToSpans,
  waitForScrollConfig,
} from "../interactive-hero/utils/interactive-hero-utils";
import { helpers } from "@contentful/rich-text-types";
import { CosmosButton, CosmosIconChevronRight } from "@cosmos/web-react";
import classnames from "classnames/bind";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { useLayoutEffect, useRef } from "react";
import styles from "./interactive-hero-lifecycle-teaser.module.css";

const cx = classnames.bind(styles);

export const InteractiveHeroLifecycleTeaser = ({
  block,
}: {
  block: BlocksLifecycleTeaser;
}) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const kickerRef = useRef<HTMLSpanElement | null>(null);
  const textWrapperRef = useRef<HTMLDivElement | null>(null);
  const buttonsRef = useRef<HTMLDivElement | null>(null);
  const imageWrapperRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    const ctx = gsap.context(() => {
      const rootElement = rootRef.current;
      const titleWordsElements = titleRef.current?.querySelectorAll("span");
      const wrapperCardElement = cardRef.current;
      const kickerElement = kickerRef.current;
      const textWrapperElement = textWrapperRef.current;
      const buttonsElement = buttonsRef.current;
      const imageWrapperElement = imageWrapperRef.current;
      const timeline = gsap.timeline();
      timeline.from(
        wrapperCardElement ?? null,
        { duration: 0.4, opacity: 0 },
        0,
      );
      timeline.from(
        kickerElement ?? null,
        { duration: 0.4, y: "+=50", opacity: 0 },
        0,
      );
      timeline.from(titleWordsElements ?? null, {
        duration: 0.4,
        y: "+=60%",
        opacity: 0,
        stagger: 0.1,
      });
      timeline.from(
        textWrapperElement ?? null,
        { duration: 0.4, y: "+=50", opacity: 0 },
        "-=0.3",
      );
      timeline.from(
        buttonsElement ?? null,
        { duration: 0.4, y: "+=50", opacity: 0 },
        "-=0.3",
      );
      timeline.from(
        imageWrapperElement ?? null,
        { duration: 0.6, opacity: 0 },
        "-=0.3",
      );
      timeline.pause();
      const scrollTrigger = new ScrollTrigger({
        trigger: rootElement,
        start: "top 70%",
        animation: timeline,
        once: true,
      });
      scrollTrigger.disable();
      waitForScrollConfig().then(() => {
        scrollTrigger.enable();
      });
    });

    return () => ctx.revert();
  }, []);

  const imageLoading = useImageLoading();

  const kickerKind = useKickerContext();

  return (
    <div ref={rootRef} className={cx("container")}>
      <div className={cx("inner")}>
        <div ref={cardRef} className={cx("card")}>
          <div className={cx("content")}>
            {(block.kicker || block.title) && (
              <div className={cx("ingredients-heading-group")}>
                {block.kicker && (
                  <Kicker
                    ref={kickerRef}
                    kind={kickerKind}
                    className={cx("kicker")}
                    text={block.kicker}
                  />
                )}
                {block.title && (
                  <h2 ref={titleRef} className={cx("title")}>
                    <ExplodedWordsToSpans text={block.title} />
                  </h2>
                )}
              </div>
            )}
            <div ref={textWrapperRef}>
              {block.body?.json.content.map((content, index) => (
                <p key={index} className={cx("text")}>
                  {content.content
                    .filter(helpers.isText)
                    .map((_content, index2) =>
                      _content.marks[0] &&
                      (_content.marks[0].type === "bold" ||
                        _content.marks[0].type === "italic") ? (
                        <span
                          key={index2}
                          className={cx(`text--${_content.marks[0].type}`)}
                        >
                          {_content.value}
                        </span>
                      ) : (
                        _content.value
                      ),
                    )}
                </p>
              ))}
            </div>
            <div ref={buttonsRef} className={cx("button-wrapper")}>
              {block.quizLinkUrl &&
                block.quizLinkText &&
                block.linkUrl &&
                block.linkText && (
                  <CosmosButton
                    href={block.linkUrl}
                    size="large"
                    kind="tertiary"
                    className={cx("button")}
                  >
                    {block.linkText}
                  </CosmosButton>
                )}
              <CosmosButton
                href={(block.quizLinkUrl || block.linkUrl) ?? undefined}
                size="large"
                kind="link"
                appearance="dark"
                icon-placement="after"
                className={cx("button")}
              >
                <CosmosIconChevronRight
                  slot="icon"
                  className={cx("button-icon")}
                />
                {block.quizLinkText ? block.quizLinkText : block.linkText}
              </CosmosButton>
            </div>
          </div>
          {block.image && (
            <div ref={imageWrapperRef} className={cx("image-wrapper")}>
              {block.image.contentType === "video/mp4" ? (
                <video
                  className={cx("asset")}
                  preload="auto"
                  autoPlay={true}
                  playsInline={true}
                  muted={true}
                  loop={true}
                  src={
                    block.image.url
                      ? getContentfulVideoUrl(block.image.url)
                      : undefined
                  }
                />
              ) : (
                <>
                  {block.image.url && (
                    <picture>
                      {(["avif", "webp"] as const).map((format, index) => (
                        <source
                          key={index}
                          type={`image/${format}`}
                          srcSet={
                            /* sizes matched to "sizes" attribute on <img> element: */ [
                              380,
                              400,
                              420,
                              650,
                              450,
                              block.image?.width,
                            ]
                              .filter(notNullish)
                              .flatMap((width) => [width, width * 2]) // <- Provide high-DPI (aka. "retina") variants
                              .filter(
                                (width) =>
                                  block.image?.width &&
                                  width <= block.image?.width, // <- Exclude widths larger than the original
                              )
                              .map((width) => {
                                const src = getContentfulImageUrl(
                                  block.image?.url ?? "",
                                  {
                                    fm: format,
                                    width,
                                  },
                                );

                                return `${src} ${width}w`;
                              })
                              .join(", ")
                          }
                        />
                      ))}
                      <img
                        loading={imageLoading}
                        sizes="(min-width: 1000px) 450px, (min-width: 650px) 650px, 100vw"
                        className={cx("asset")}
                        alt={block.image.description ?? ""}
                      />
                    </picture>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
